import { isBanner } from '@/components/card';
import { FavoriteButton } from '@/components/favorite-button';
import { NowPlayingIndicator } from '@/components/now-playing-indicator';
import StopPropagation from '@/components/stop-propagation';
import { useTrackRowPlayback } from '@/hooks/use-track-row-playback';
import { EnumButtonName } from '@calm/evexia-analytics';
import { useFavoriteGuide } from '@rhg/hooks/use-favorite-guide';
import { useProvidedProgramContextWithWorkflow } from '@rhg/hooks/use-provided-program-context/with-workflow';
import { Box, Theme } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { getGuideId } from '@rhg/utils/media';
import { ProgramContextAwareSessionCardProps } from '../program-context-aware';

const styleFavoriteButton = {
    p: 0,
    zIndex: (theme: Theme) => theme.zIndex.appBar - 1,
};

const styleFavoriteButtonBanner = { p: 1 };

const styleActionsRow = {
    display: 'flex',
    alignItems: 'center',
    gap: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};

type SessionCardAdditionalActionProps = Pick<
    ProgramContextAwareSessionCardProps,
    | 'variant'
    | 'session'
    | 'additionalAction'
    | 'hideFavoriteButton'
    | 'showPlayingIndicator'
>;

/** Subscribes to playback state — mounted only when `showPlayingIndicator` is
 *  set, so the vast majority of cards never subscribe to audio state. */
function SessionNowPlayingIndicator({
    session,
}: Pick<SessionCardAdditionalActionProps, 'session'>) {
    const { isActiveTrack, isPlaying } = useTrackRowPlayback(session);

    if (!isActiveTrack) return null;

    return <NowPlayingIndicator title={session.title} isPlaying={isPlaying} />;
}

export function SessionCardAdditionalAction({
    variant,
    session,
    additionalAction,
    hideFavoriteButton,
    showPlayingIndicator,
}: SessionCardAdditionalActionProps) {
    const { programType, programState } =
        useProvidedProgramContextWithWorkflow();

    const { favoriteGuide, canFavorite: _canFavorite } = useFavoriteGuide({
        programType,
        programState,
    });
    const canFavorite = _canFavorite && !hideFavoriteButton;

    const favoriteButton = (
        <FavoriteButton
            guide={session}
            size="legacyMedium"
            trackButtonFavorite={EnumButtonName.Favorite}
            trackButtonUnfavorite={EnumButtonName.Favorite}
            // Pack Item Properties for the favorite Button : Clicked. Feed /
            // Pack / TagName context auto-attaches via the feed page's
            // ambient AnalyticsEventPropsProvider when on a collection page.
            trackProps={{
                PackItemName: session.title,
                PackItemType: 'guide',
                GuideId: getGuideId(session),
                ProgramId: session.programId,
                MediaType: session.assets?.[0]?.type,
            }}
            prefersDarkToast={false}
            favoriteGuide={favoriteGuide}
            canFavorite={canFavorite}
            sx={sxAppend(
                styleFavoriteButton,
                isBanner(variant) ? styleFavoriteButtonBanner : undefined
            )}
        />
    );

    if (!showPlayingIndicator) {
        return (
            additionalAction ?? (
                <StopPropagation>{favoriteButton}</StopPropagation>
            )
        );
    }

    return (
        <Box sx={styleActionsRow}>
            <SessionNowPlayingIndicator session={session} />
            {additionalAction ?? (
                <StopPropagation>{favoriteButton}</StopPropagation>
            )}
        </Box>
    );
}
